import { useState } from 'react';
import ShareCardModal from './ShareCardModal';
import SubscriptionGate from './SubscriptionGate';

function Stars({ rating }) {
  return (
    <div className="review-stars">
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} className={`star ${i <= rating ? 'filled' : ''}`}>★</span>
      ))}
    </div>
  );
}

const PLATFORM_BADGES = {
  google: { label: '🔍 Google', className: 'google' },
  airbnb: { label: '🏠 Airbnb', className: 'airbnb' },
  trustpilot: { label: '⭐ Trustpilot', className: 'trustpilot' },
  manual: { label: 'Manual', className: 'manual' },
};

function formatReviewDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ReviewCard({ review, onApprove, onSkip, onGenerate, onRegenerate, onDelete, showTranslation, businessName, insightsSince, isSubscribed = true }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(review.generated_response || '');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const handleGenerate = async () => { setLoading(true); try { await onGenerate(review.id); } finally { setLoading(false); } };
  const handleRegenerate = async () => { setLoading(true); try { await onRegenerate(review.id); } finally { setLoading(false); } };
  const handleApprove = async () => { setLoading(true); const edited = editing && editText !== review.generated_response ? editText : null; try { await onApprove(review.id, edited); } finally { setLoading(false); setEditing(false); } };
  const handleSkip = async () => { setLoading(true); try { await onSkip(review.id); } finally { setLoading(false); } };

  const handleCopy = async () => {
    const text = review.final_response || review.generated_response;
    if (!text) return;
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  const status = (review.status || '').toLowerCase();
  const hasResponse = review.generated_response || review.final_response;
  const displayResponse = review.final_response || review.generated_response;
  const source = review.source || 'google';
  const badge = PLATFORM_BADGES[source] || PLATFORM_BADGES.manual;
  const showBadge = source !== 'google';
  const hasTranslatedReview = showTranslation && review.translated_review_text;
  const canShare = review.rating >= 4 && review.review_text;
  const showSmartBadge = insightsSince && review.generated_at && review.generated_at >= insightsSince;

  return (
    <div className="review-card card">
      <div className="review-header">
        <div>
          <div className="review-author">
            {review.reviewer_name}
            {showBadge && <span className={`source-badge ${badge.className}`}>{badge.label}</span>}
          </div>
          <div className="review-date">{formatReviewDate(review.review_created_at || review.created_at)}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Stars rating={review.rating} />
          <span className={`review-status ${status}`}>{status}</span>
        </div>
      </div>

      {review.review_text ? (
        <div className="review-text">"{review.review_text}"</div>
      ) : (
        <div className="review-text" style={{ fontStyle: 'italic', opacity: 0.6 }}>(Rating only — no text)</div>
      )}

      {hasTranslatedReview && (
        <div className="translated-review">
          <div className="translated-review-label">🌐 Translated ({review.translated_language})</div>
          "{review.translated_review_text}"
        </div>
      )}

      {hasResponse && (
        <div className="response-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="response-label">{status === 'approved' || status === 'posted' ? 'Approved Response' : 'AI-Generated Response'}</div>
            {showSmartBadge && (
              <span title="This response uses competitor intelligence to subtly highlight your strengths" style={{ fontSize: 11, fontWeight: 600, color: '#9333ea', background: 'rgba(147,51,234,0.08)', padding: '2px 8px', borderRadius: 4, cursor: 'default', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                ⚡ Smart Insight
              </span>
            )}
          </div>
          {editing ? (
            <textarea className="response-edit" value={editText} onChange={e => setEditText(e.target.value)} rows={4} />
          ) : (
            <div className="response-text">{displayResponse}</div>
          )}
        </div>
      )}

      <div className="review-actions">
        {status === 'pending' && (
          <SubscriptionGate isSubscribed={isSubscribed} feature="Generating AI responses">
            <button className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
              {loading ? 'Generating...' : '✨ Generate Response'}
            </button>
          </SubscriptionGate>
        )}

        {status === 'generated' && (
          <>
            {editing ? (
              <>
                <SubscriptionGate isSubscribed={isSubscribed} feature="Approving responses">
                  <button className="btn btn-success" onClick={handleApprove} disabled={loading}>✓ Approve Edited</button>
                </SubscriptionGate>
                <button className="btn btn-ghost" onClick={() => { setEditing(false); setEditText(review.generated_response); }}>Cancel</button>
              </>
            ) : (
              <>
                <SubscriptionGate isSubscribed={isSubscribed} feature="Approving responses">
                  <button className="btn btn-success" onClick={handleApprove} disabled={loading}>✓ Approve</button>
                </SubscriptionGate>
                <button className="btn btn-ghost" onClick={() => setEditing(true)}>✏️ Edit</button>
                <SubscriptionGate isSubscribed={isSubscribed} feature="Regenerating responses">
                  <button className="btn btn-ghost" onClick={handleRegenerate} disabled={loading}>{loading ? 'Regenerating...' : '🔄 Regenerate'}</button>
                </SubscriptionGate>
                <button className="btn btn-ghost" onClick={handleCopy} title="Copy response to clipboard">{copied ? '✓ Copied!' : '📋 Copy'}</button>
              </>
            )}
            <button className="btn btn-ghost" onClick={handleSkip} disabled={loading} style={{ marginLeft: 'auto' }}>Skip</button>
          </>
        )}

        {(status === 'approved' || status === 'posted') && hasResponse && (
          <button className="btn btn-ghost" onClick={handleCopy} title="Copy response to clipboard">{copied ? '✓ Copied!' : '📋 Copy Response'}</button>
        )}

        {canShare && (
          <button className="btn btn-ghost" onClick={() => setShowShareModal(true)} title="Create shareable review image" style={{ marginLeft: status === 'pending' || (!hasResponse && status !== 'generated') ? 0 : 'auto' }}>📸 Share</button>
        )}

        {onDelete && (
          <button className="btn btn-ghost" onClick={() => onDelete(review.id, review.reviewer_name)} style={{ color: '#dc2626' }}>🗑 Delete</button>
        )}
      </div>

      {showShareModal && <ShareCardModal review={review} businessName={businessName} onClose={() => setShowShareModal(false)} />}
    </div>
  );
}
